import { z } from 'zod';

/**
 * Search parameters interface for event filtering and URL serialization
 *
 * @description Defines the structure of search parameters that can be serialized to/from URL query strings.
 * Used for shareable event searches across web and mobile platforms.
 */
export interface SearchParams {
  // Categories
  categories?: string[];

  // Price
  minPrice?: number;
  maxPrice?: number;
  includeFree?: boolean;
  currency?: string;

  // Date
  startDate?: string;
  endDate?: string;
  timezone?: string;

  // Location
  latitude?: number;
  longitude?: number;
  radius?: number;

  // Pagination
  limit?: number;
  offset?: number;
}

/**
 * Zod schema for search parameters validation with cross-field refinements
 *
 * @description Validates search parameters including business logic constraints like
 * maxPrice >= minPrice, endDate >= startDate, and location parameter grouping.
 */
export const searchParamsSchema = z
  .object({
    // Categories
    categories: z.array(z.string().min(1)).max(10, 'Maximum 10 categories allowed').optional(),

    // Price
    minPrice: z.number().int().min(0, 'Minimum price must be >= 0').optional(),
    maxPrice: z.number().int().min(0, 'Maximum price must be >= 0').optional(),
    includeFree: z.boolean().optional(),
    currency: z.string().length(3, 'Currency must be a 3-letter code').optional(),

    // Date - supports both ISO 8601 datetime and date-only (YYYY-MM-DD) formats
    startDate: z
      .string()
      .refine(
        (val) => {
          // Accept ISO 8601 datetime format
          if (val.includes('T') || val.includes('Z') || val.includes('+')) {
            return !isNaN(Date.parse(val));
          }
          // Accept date-only format (YYYY-MM-DD)
          const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateOnlyRegex.test(val)) {
            return !isNaN(Date.parse(val));
          }
          return false;
        },
        {
          message: 'Start date must be a valid ISO 8601 datetime or date-only format (YYYY-MM-DD)',
        },
      )
      .optional(),
    endDate: z
      .string()
      .refine(
        (val) => {
          // Accept ISO 8601 datetime format
          if (val.includes('T') || val.includes('Z') || val.includes('+')) {
            return !isNaN(Date.parse(val));
          }
          // Accept date-only format (YYYY-MM-DD)
          const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (dateOnlyRegex.test(val)) {
            return !isNaN(Date.parse(val));
          }
          return false;
        },
        { message: 'End date must be a valid ISO 8601 datetime or date-only format (YYYY-MM-DD)' },
      )
      .optional(),
    timezone: z.string().optional(),

    // Location
    latitude: z
      .number()
      .min(-90, 'Latitude must be >= -90')
      .max(90, 'Latitude must be <= 90')
      .optional(),
    longitude: z
      .number()
      .min(-180, 'Longitude must be >= -180')
      .max(180, 'Longitude must be <= 180')
      .optional(),
    radius: z
      .number()
      .min(0.1, 'Radius must be >= 0.1 km')
      .max(100, 'Radius must be <= 100 km')
      .optional(),

    // Pagination
    limit: z
      .number()
      .int()
      .min(1, 'Limit must be >= 1')
      .max(100, 'Limit must be <= 100')
      .optional(),
    offset: z.number().int().min(0, 'Offset must be >= 0').optional(),
  })
  .refine((data) => !data.maxPrice || !data.minPrice || data.maxPrice >= data.minPrice, {
    message: 'maxPrice must be >= minPrice',
    path: ['maxPrice'],
  })
  .refine((data) => !data.endDate || !data.startDate || data.endDate >= data.startDate, {
    message: 'endDate must be after or equal to startDate',
    path: ['endDate'],
  })
  .refine(
    (data) => {
      const hasLat = data.latitude !== undefined;
      const hasLng = data.longitude !== undefined;
      const hasRadius = data.radius !== undefined;

      // All location params required together
      if (hasLat || hasLng || hasRadius) {
        return hasLat && hasLng && hasRadius;
      }
      return true;
    },
    {
      message: 'latitude, longitude, and radius must all be provided together',
      path: ['latitude'],
    },
  );

/**
 * Serializes SearchParams object to URL query string
 *
 * @description Converts search parameters to a URL-encoded query string suitable for sharing.
 * Handles arrays as comma-separated values, encodes special characters, and omits undefined values.
 *
 * @param params - Search parameters to serialize
 * @returns URL query string (without leading '?')
 *
 * @example
 * ```typescript
 * const params: SearchParams = {
 *   categories: ['music', 'sports'],
 *   minPrice: 2000,
 *   maxPrice: 10000,
 *   latitude: 35.2271,
 *   longitude: -80.8431,
 *   radius: 10,
 *   startDate: '2025-01-15T00:00:00.000Z',
 *   endDate: '2025-01-30T23:59:59.999Z'
 * };
 * const query = serializeSearchParams(params);
 * // "categories=music,sports&minPrice=2000&maxPrice=10000&lat=35.2271&lng=-80.8431&radius=10&startDate=2025-01-15T00:00:00.000Z&endDate=2025-01-30T23:59:59.999Z"
 * ```
 */
export function serializeSearchParams(params: SearchParams): string {
  const searchParams = new URLSearchParams();

  // Arrays: comma-separated
  if (params.categories?.length) {
    searchParams.set('categories', params.categories.join(','));
  }

  // Numbers: string representation
  if (params.minPrice !== undefined) {
    searchParams.set('minPrice', params.minPrice.toString());
  }
  if (params.maxPrice !== undefined) {
    searchParams.set('maxPrice', params.maxPrice.toString());
  }

  // Booleans: 'true' or 'false' (only if explicitly set)
  if (params.includeFree === true) {
    searchParams.set('includeFree', 'true');
  } else if (params.includeFree === false) {
    searchParams.set('includeFree', 'false');
  }

  // Dates: ISO 8601 format
  if (params.startDate) {
    searchParams.set('startDate', params.startDate);
  }
  if (params.endDate) {
    searchParams.set('endDate', params.endDate);
  }

  // Location: decimal coordinates
  if (params.latitude !== undefined) {
    searchParams.set('lat', params.latitude.toString());
  }
  if (params.longitude !== undefined) {
    searchParams.set('lng', params.longitude.toString());
  }
  if (params.radius !== undefined) {
    searchParams.set('radius', params.radius.toString());
  }

  // Strings: direct encoding
  if (params.timezone) {
    searchParams.set('timezone', params.timezone);
  }
  if (params.currency) {
    searchParams.set('currency', params.currency);
  }

  // Pagination
  if (params.limit !== undefined) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.set('offset', params.offset.toString());
  }

  return searchParams.toString();
}

/**
 * Parses URL query string to SearchParams object
 *
 * @description Parses and validates a URL query string into typed SearchParams.
 * Handles comma-separated arrays, number parsing, boolean coercion, and validates all constraints.
 *
 * @param queryString - URL query string (with or without leading '?')
 * @returns Validated SearchParams object
 * @throws ZodError if validation fails
 *
 * @example
 * ```typescript
 * const query = "categories=music,sports&minPrice=2000&lat=35.2271&lng=-80.8431&radius=10";
 * const params = parseSearchParams(query);
 * // {
 * //   categories: ['music', 'sports'],
 * //   minPrice: 2000,
 * //   latitude: 35.2271,
 * //   longitude: -80.8431,
 * //   radius: 10
 * // }
 * ```
 */
export function parseSearchParams(queryString: string): SearchParams {
  const params = new URLSearchParams(
    queryString.startsWith('?') ? queryString.slice(1) : queryString,
  );

  // Build the object to parse, only including valid values
  const parsedObject: Partial<SearchParams> = {};

  // Parse arrays
  const categories = params.get('categories')?.split(',').filter(Boolean);
  if (categories?.length) parsedObject.categories = categories;

  // Parse numbers
  const minPrice = params.get('minPrice') ? parseInt(params.get('minPrice')!, 10) : undefined;
  if (minPrice !== undefined && !isNaN(minPrice)) parsedObject.minPrice = minPrice;

  const maxPrice = params.get('maxPrice') ? parseInt(params.get('maxPrice')!, 10) : undefined;
  if (maxPrice !== undefined && !isNaN(maxPrice)) parsedObject.maxPrice = maxPrice;

  const radius = params.get('radius') ? parseFloat(params.get('radius')!) : undefined;
  if (radius !== undefined && !isNaN(radius)) parsedObject.radius = radius;

  const latitude = params.get('lat') ? parseFloat(params.get('lat')!) : undefined;
  if (latitude !== undefined && !isNaN(latitude)) parsedObject.latitude = latitude;

  const longitude = params.get('lng') ? parseFloat(params.get('lng')!) : undefined;
  if (longitude !== undefined && !isNaN(longitude)) parsedObject.longitude = longitude;

  // Parse booleans (only valid values)
  const includeFreeVal = params.get('includeFree');
  if (includeFreeVal === 'true') parsedObject.includeFree = true;
  else if (includeFreeVal === 'false') parsedObject.includeFree = false;
  // Otherwise, don't include it (remains undefined)

  // Parse strings
  const startDate = params.get('startDate');
  if (startDate) parsedObject.startDate = startDate;

  const endDate = params.get('endDate');
  if (endDate) parsedObject.endDate = endDate;

  const timezone = params.get('timezone');
  if (timezone) parsedObject.timezone = timezone;

  const currency = params.get('currency');
  if (currency) parsedObject.currency = currency;

  // Pagination
  const limit = params.get('limit') ? parseInt(params.get('limit')!, 10) : undefined;
  if (limit !== undefined && !isNaN(limit)) parsedObject.limit = limit;

  const offset = params.get('offset') ? parseInt(params.get('offset')!, 10) : undefined;
  if (offset !== undefined && !isNaN(offset)) parsedObject.offset = offset;

  return searchParamsSchema.parse(parsedObject);
}

/**
 * Type alias for the inferred SearchParams type from the schema
 */
export type SearchParamsType = z.infer<typeof searchParamsSchema>;
